import { getCheckoutSession } from '@commenda/api';
import {
  ExternalPaymentStatus,
  PaymentStatus,
  TCheckoutSessionMetadata,
} from '@commenda/types/src/billing/types';
import { TSetNotification } from '@commenda/types/src/random';
import { useCallback, useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 2000; // 2 seconds between polls
const MAX_POLLING_TIME = 60000; // 60 seconds maximum polling time
const MAX_RETRIES = 30; // Maximum number of retries for API failures

export const usePaymentConfirmation = ({
  checkoutSessionId,
  onPaymentFailure,
  onPaymentSuccessOrThrow,
  setNotification,
}: {
  checkoutSessionId: string;
  onPaymentFailure?: () => void;
  // This function should throw an error if the function fails.
  onPaymentSuccessOrThrow?: (params?: {
    checkoutSessionId: string;
    data: TCheckoutSessionMetadata;
  }) => Promise<void>;
  setNotification: TSetNotification;
}) => {
  const arePaymentDetailsFetchedRef = useRef<boolean>(false);
  // This will help us define if the payment failed or the webhook processing failed.
  const isPaymentSuccessRef = useRef<boolean>(false);

  // Refs for tracking polling state
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const retryCountRef = useRef<number>(0);
  const isPollingActiveRef = useRef<boolean>(false);

  const [sessionMetadata, setSessionMetadata] =
    useState<TCheckoutSessionMetadata>();

  // Frontend payment status (for user interface)
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.LOADING
  );

  const handleSuccessfulPayment = useCallback(async () => {
    await onPaymentSuccessOrThrow?.({
      checkoutSessionId,
      data: sessionMetadata,
    });
  }, [checkoutSessionId, onPaymentSuccessOrThrow, sessionMetadata]);

  // 1. We fetch the session details and see if the payment status is succeeded or not.
  // We update the metadata and the payment update state.
  // If the checkPayment returns true, we stop the polling.
  const checkPayment = useCallback(async (): Promise<boolean> => {
    if (!checkoutSessionId) return false;
    try {
      const response = await getCheckoutSession(checkoutSessionId);

      if (!response?.data) {
        retryCountRef.current += 1;
        // If we've hit the retry limit, fail the payment process
        if (retryCountRef.current >= MAX_RETRIES) {
          setPaymentStatus((prev) =>
            prev !== PaymentStatus.LOADING
              ? PaymentStatus.FULFILLMENT_FAILED
              : PaymentStatus.MAX_RETRIES_REACHED
          );
          onPaymentFailure?.();
          return true;
        }
        return false;
      }
      const { data } = response;

      setSessionMetadata(data.metadata);
      if (data.status === ExternalPaymentStatus.PAID) {
        setPaymentStatus(PaymentStatus.PAYMENT_SUCCESS);
        isPaymentSuccessRef.current = true;
      }

      if (data.status === ExternalPaymentStatus.ACKNOWLEDGED) {
        setPaymentStatus(PaymentStatus.FULFILLMENT_PROCESSED);
        arePaymentDetailsFetchedRef.current = true;
        await handleSuccessfulPayment();
        return true;
      }

      if (data.status === ExternalPaymentStatus.FAILED) {
        setPaymentStatus(PaymentStatus.PAYMENT_FAILED);
        arePaymentDetailsFetchedRef.current = true;
        onPaymentFailure?.();
        return true;
      }

      return false;
    } catch (err) {
      retryCountRef.current += 1;
      // If we've hit the retry limit, fail the payment process
      if (retryCountRef.current >= MAX_RETRIES) {
        setPaymentStatus(PaymentStatus.MAX_RETRIES_REACHED);
        onPaymentFailure?.();
        return true;
      }
      return false;
    }
  }, [checkoutSessionId, handleSuccessfulPayment, onPaymentFailure]);

  // Function to start the polling mechanism
  const startPolling = useCallback(() => {
    // If already polling, don't start again
    if (isPollingActiveRef.current) return;

    // Set the start time for the polling
    pollingStartTimeRef.current = Date.now();
    isPollingActiveRef.current = true;

    // Initial check
    checkPayment();

    // Set up interval for repeated checking
    pollingIntervalRef.current = setInterval(async () => {
      // Check if we've exceeded our maximum polling time
      if (
        pollingStartTimeRef.current &&
        Date.now() - pollingStartTimeRef.current > MAX_POLLING_TIME
      ) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Only update status if we haven't already established a final state
        if (paymentStatus === PaymentStatus.LOADING) {
          setPaymentStatus(PaymentStatus.CONNECTION_TIMEOUT);
          setNotification({
            show: true,
            title: 'Timeout',
            valid: false,
            message:
              'Something went wrong. Please try again, or contact Team Commenda if you need help.',
          });

          if (onPaymentFailure) {
            onPaymentFailure();
          }
        }

        isPollingActiveRef.current = false;
        return;
      }

      // Check the payment status
      const shouldStopPolling = await checkPayment();

      // If we've reached a terminal status, stop polling
      if (shouldStopPolling && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingActiveRef.current = false;
      }
    }, POLLING_INTERVAL);
  }, [checkPayment, paymentStatus, setNotification, onPaymentFailure]);

  // Initialize polling when the hook is first mounted
  useEffect(() => {
    if (!checkoutSessionId || arePaymentDetailsFetchedRef.current) return;
    startPolling();
  }, [checkoutSessionId, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingActiveRef.current = false;
    };
  }, []);

  return {
    paymentStatus,
    sessionMetadata,
  };
};

